import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderKanban, Folder, FolderPlus, Upload, Search, Eye, Download, Sparkles,
  MoreHorizontal, Trash2, Pencil, FolderInput, BookOpenCheck, Loader2, X,
  FileText, FileSpreadsheet, Image as ImageIcon, File as FileIcon, Lock,
  CheckCircle2, AlertCircle, Users,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

/* ------------------------------- helpers -------------------------------- */
const fmtSize = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
};
const ExtIcon = ({ ext }) => {
  if (ext === ".pdf") return <FileText size={18} className="text-red-500" />;
  if ([".xlsx", ".xls", ".csv"].includes(ext)) return <FileSpreadsheet size={18} className="text-emerald-600" />;
  if ([".png", ".jpg", ".jpeg", ".webp"].includes(ext)) return <ImageIcon size={18} className="text-sky-500" />;
  return <FileIcon size={18} className="text-text-muted" />;
};
const IMG_EXT = [".png", ".jpg", ".jpeg", ".webp"];

// Quando il server risponde con un errore, l'utente deve leggerne il motivo
// (le risposte di download sono blob: il dettaglio va estratto dal corpo).
const motivoErrore = async (e, fallback) => {
  try {
    const d = e?.response?.data;
    if (d instanceof Blob) {
      const j = JSON.parse(await d.text());
      if (j?.detail) return j.detail;
    } else if (d?.detail) {
      return d.detail;
    }
  } catch { /* corpo non leggibile */ }
  const s = e?.response?.status;
  return s
    ? `${fallback}: il server ha risposto con un errore (codice ${s}). Riprova o segnalalo all'assistenza.`
    : `${fallback}: impossibile contattare il server. Controlla la connessione.`;
};

/* ----------------------------- Folder dialog ----------------------------- */
const FolderDialog = ({ open, onClose, onSaved, folder }) => {
  const [name, setName] = useState(folder?.name || "");
  const [adminOnly, setAdminOnly] = useState(folder?.visibility === "admin");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setName(folder?.name || "");
    setAdminOnly(folder?.visibility === "admin");
  }, [folder, open]);
  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (folder?.id) {
        await api.patch(`/docs/folders/${folder.id}`, { name: name.trim(), visibility: adminOnly ? "admin" : "all" });
        toast.success("Cartella aggiornata");
      } else {
        await api.post("/docs/folders", { name: name.trim(), visibility: adminOnly ? "admin" : "all" });
        toast.success("Cartella creata");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio cartella");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="folder-dialog">
        <DialogHeader>
          <DialogTitle>{folder?.id ? "Rinomina cartella" : "Nuova cartella"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome cartella"
                 data-testid="folder-name-input" autoFocus
                 onKeyDown={(e) => e.key === "Enter" && save()} />
          <label className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2 text-text-secondary">
              <Lock size={14} /> Riservata agli amministratori
            </span>
            <Switch checked={adminOnly} onCheckedChange={setAdminOnly} data-testid="folder-admin-switch" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving || !name.trim()} data-testid="folder-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ----------------------------- Upload dialog ----------------------------- */
const UploadDialog = ({ open, onClose, onSaved, folders, defaultFolderId }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState(defaultFolderId || "");
  const [requiresReading, setRequiresReading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    if (open) {
      setFile(null); setTitle(""); setRequiresReading(false);
      setFolderId(defaultFolderId || "");
    }
  }, [open, defaultFolderId]);
  const save = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (folderId) form.append("folder_id", folderId);
      if (title.trim()) form.append("title", title.trim());
      form.append("requires_reading", requiresReading ? "true" : "false");
      await api.post("/docs/files", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      window.dispatchEvent(new Event("wl-unread-refresh"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    } finally {
      setUploading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle>Carica documento</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <button type="button" onClick={() => fileRef.current?.click()}
                  data-testid="upload-file-picker"
                  className="w-full border border-dashed border-border rounded-lg px-4 py-6 text-sm text-text-secondary hover:border-accent hover:text-text-primary transition-colors text-center">
            {file ? (
              <span className="flex items-center justify-center gap-2 text-text-primary">
                <FileText size={15} /> {file.name} · {fmtSize(file.size)}
              </span>
            ) : (
              <>Clicca per scegliere un file<br />
                <span className="text-xs text-text-muted">PDF, Word, Excel, CSV, TXT, MD o immagini — max 25 MB</span></>
            )}
          </button>
          <input ref={fileRef} type="file" className="hidden"
                 accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md,.png,.jpg,.jpeg,.webp"
                 onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder="Titolo (opzionale, altrimenti il nome file)" data-testid="upload-title-input" />
          <select value={folderId} onChange={(e) => setFolderId(e.target.value)}
                  data-testid="upload-folder-select"
                  className="w-full h-9 rounded-md border border-border bg-surface px-3 text-sm text-text-primary">
            <option value="">Radice (nessuna cartella)</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>{f.name}{f.visibility === "admin" ? " (riservata)" : ""}</option>
            ))}
          </select>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2 text-text-secondary">
              <BookOpenCheck size={14} /> Lettura obbligatoria per il team
            </span>
            <Switch checked={requiresReading} onCheckedChange={setRequiresReading} data-testid="upload-required-switch" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={uploading || !file} data-testid="upload-save-btn">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : "Carica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ----------------------------- Preview modal ----------------------------- */
const PreviewModal = ({ doc, onClose, onMarkedRead }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [text, setText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const isBinary = doc && (doc.ext === ".pdf" || IMG_EXT.includes(doc.ext));
  useEffect(() => {
    if (!doc) return undefined;
    let url = null;
    let cancelled = false;
    setLoading(true); setBlobUrl(null); setText(null);
    (async () => {
      try {
        if (isBinary) {
          const res = await api.get(`/docs/files/${doc.id}/download?inline=1`, { responseType: "blob" });
          url = URL.createObjectURL(res.data);
          if (!cancelled) setBlobUrl(url);
        } else {
          const { data } = await api.get(`/docs/files/${doc.id}/text`);
          if (!cancelled) setText(data.has_text ? data.text : null);
        }
      } catch (e) {
        if (!cancelled) toast.error(await motivoErrore(e, "Anteprima non riuscita"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id]);
  const markRead = async () => {
    setMarking(true);
    try {
      await api.post(`/docs/files/${doc.id}/read`);
      toast.success("Segnato come letto");
      window.dispatchEvent(new Event("wl-unread-refresh"));
      onMarkedRead(doc.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setMarking(false);
    }
  };
  if (!doc) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[88vh] flex flex-col" data-testid="preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <ExtIcon ext={doc.ext} />
            <span className="truncate">{doc.title || doc.filename}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto min-h-[300px] rounded-lg border border-border bg-subtle/40">
          {loading ? (
            <div className="h-full min-h-[300px] flex items-center justify-center text-text-muted">
              <Loader2 size={22} className="animate-spin" />
            </div>
          ) : doc.ext === ".pdf" && blobUrl ? (
            <iframe src={blobUrl} title={doc.filename} className="w-full h-[62vh]" data-testid="preview-pdf" />
          ) : IMG_EXT.includes(doc.ext) && blobUrl ? (
            <div className="flex items-center justify-center p-4">
              <img src={blobUrl} alt={doc.filename} className="max-w-full max-h-[60vh] object-contain rounded" data-testid="preview-image" />
            </div>
          ) : text ? (
            <pre className="p-4 text-xs text-text-primary whitespace-pre-wrap font-mono" data-testid="preview-text">{text}</pre>
          ) : (
            <div className="h-full min-h-[300px] flex flex-col items-center justify-center gap-2 text-text-muted text-sm">
              <AlertCircle size={20} />
              Anteprima non disponibile per questo file: scaricalo per aprirlo.
            </div>
          )}
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between items-center">
          <div className="text-xs text-text-muted">
            {doc.uploaded_by_name} · {fmtDate(doc.uploaded_at)} · {fmtSize(doc.size_bytes)}
          </div>
          {doc.requires_reading && !doc.read_at ? (
            <Button onClick={markRead} disabled={marking} data-testid="btn-mark-read"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {marking ? <Loader2 size={14} className="animate-spin" /> : <><CheckCircle2 size={14} className="mr-1.5" />Segna come letto</>}
            </Button>
          ) : doc.requires_reading ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 font-medium" data-testid="already-read-label">
              <CheckCircle2 size={14} /> Letto
            </span>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------ Reads modal ------------------------------ */
const ReadsModal = ({ doc, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!doc) return;
    api.get(`/docs/files/${doc.id}/reads`).then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore"));
  }, [doc]);
  if (!doc) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="reads-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={16} /> Chi l&apos;ha letto — {doc.title || doc.filename}
          </DialogTitle>
        </DialogHeader>
        {!data ? (
          <div className="py-8 flex justify-center"><Loader2 size={20} className="animate-spin text-text-muted" /></div>
        ) : (
          <div className="space-y-4 max-h-[55vh] overflow-auto py-1">
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-emerald-600 mb-2">
                Hanno letto ({data.read.length})
              </div>
              {data.read.length === 0 && <div className="text-sm text-text-muted">Nessuno, per ora.</div>}
              {data.read.map((r) => (
                <div key={r.user_id} className="flex items-center justify-between text-sm py-1" data-testid={`read-user-${r.user_id}`}>
                  <span className="text-text-primary">{r.user_name}</span>
                  <span className="text-xs text-text-muted">{fmtDate(r.read_at)}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-amber-600 mb-2">
                Non ancora ({data.unread.length})
              </div>
              {data.unread.length === 0 && <div className="text-sm text-text-muted">Tutti hanno letto ✓</div>}
              {data.unread.map((u) => (
                <div key={u.user_id} className="text-sm py-1 text-text-secondary" data-testid={`unread-user-${u.user_id}`}>
                  {u.user_name}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* --------------------------------- Page ---------------------------------- */
export default function Documents() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ["admin", "super_admin"].includes((user?.role || "").toLowerCase());
  const [tree, setTree] = useState({ folders: [], root_count: 0, unread_count: 0 });
  const [activeFolder, setActiveFolder] = useState(undefined); // undefined = tutti
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [treeFailed, setTreeFailed] = useState(false);
  const [search, setSearch] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [folderDialog, setFolderDialog] = useState(null); // {folder|null}
  const [uploadOpen, setUploadOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [readsDoc, setReadsDoc] = useState(null);
  const [renameDoc, setRenameDoc] = useState(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [moveDoc, setMoveDoc] = useState(null);
  const [moveTarget, setMoveTarget] = useState("");
  const [askingId, setAskingId] = useState(null);

  const loadTree = useCallback(async () => {
    try {
      const { data } = await api.get("/docs/tree");
      setTree(data);
      setTreeFailed(false);
    } catch (e) {
      if (e?.response?.status !== 403) setTreeFailed(true);
    }
  }, []);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeFolder !== undefined) params.folder_id = activeFolder || "";
      if (search.trim()) params.search = search.trim();
      if (onlyUnread) params.only_unread = true;
      const { data } = await api.get("/docs/files", { params });
      setFiles(data.items || []);
      setLoadFailed(false);
    } catch (e) {
      setLoadFailed(true);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [activeFolder, search, onlyUnread]);

  useEffect(() => { loadTree(); }, [loadTree]);
  useEffect(() => {
    const t = setTimeout(loadFiles, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadFiles, search]);

  const refresh = () => { loadTree(); loadFiles(); };

  const markedRead = (docId) => {
    setFiles((fs) => fs.map((f) => (f.id === docId ? { ...f, read_at: new Date().toISOString() } : f)));
    setPreview((p) => (p && p.id === docId ? { ...p, read_at: new Date().toISOString() } : p));
    loadTree();
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/docs/files/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(await motivoErrore(e, "Download non riuscito"));
    }
  };

  const askAi = async (doc) => {
    setAskingId(doc.id);
    try {
      const { data } = await api.post(`/docs/files/${doc.id}/ask-ai`);
      navigate("/assistant", {
        state: {
          initialAttachment: data,
          initialMessage: `Ho allegato il documento "${data.title || data.filename}" dall'archivio aziendale: `,
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore preparazione documento per l'AI");
    } finally {
      setAskingId(null);
    }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Eliminare definitivamente "${doc.title || doc.filename}"?`)) return;
    try {
      await api.delete(`/docs/files/${doc.id}`);
      toast.success("Documento eliminato");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const deleteFolder = async (f) => {
    if (!window.confirm(`Eliminare la cartella "${f.name}"? (deve essere vuota)`)) return;
    try {
      await api.delete(`/docs/folders/${f.id}`);
      toast.success("Cartella eliminata");
      if (activeFolder === f.id) setActiveFolder(undefined);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione cartella");
    }
  };

  const saveRename = async () => {
    if (!renameTitle.trim()) return;
    try {
      await api.patch(`/docs/files/${renameDoc.id}`, { title: renameTitle.trim() });
      toast.success("Titolo aggiornato");
      setRenameDoc(null);
      loadFiles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const saveMove = async () => {
    try {
      await api.patch(`/docs/files/${moveDoc.id}`, { folder_id: moveTarget });
      toast.success("Documento spostato");
      setMoveDoc(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore spostamento");
    }
  };

  const toggleRequired = async (doc) => {
    try {
      await api.patch(`/docs/files/${doc.id}`, { requires_reading: !doc.requires_reading });
      toast.success(doc.requires_reading ? "Lettura non più obbligatoria" : "Segnato come lettura obbligatoria");
      window.dispatchEvent(new Event("wl-unread-refresh"));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const activeFolderObj = useMemo(
    () => tree.folders.find((f) => f.id === activeFolder), [tree.folders, activeFolder]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="docs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-xl font-semibold text-text-primary flex items-center gap-2.5">
            <FolderKanban size={22} className="text-accent" /> Documenti
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Archivio aziendale: procedure, comunicazioni e materiali del team.
            {tree.unread_count > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-amber-600 font-medium" data-testid="docs-unread-hint">
                <BookOpenCheck size={13} /> {tree.unread_count} da leggere
              </span>
            )}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setFolderDialog({})} data-testid="btn-new-folder">
              <FolderPlus size={15} className="mr-1.5" /> Nuova cartella
            </Button>
            <Button size="sm" onClick={() => setUploadOpen(true)} data-testid="btn-upload-doc">
              <Upload size={15} className="mr-1.5" /> Carica documento
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Cartelle */}
        <aside className="lg:col-span-3">
          <div className="rounded-xl border border-border bg-surface p-2" data-testid="docs-folder-list">
            <button type="button" onClick={() => setActiveFolder(undefined)}
                    data-testid="docs-folder-all"
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeFolder === undefined ? "bg-subtle font-medium text-text-primary" : "text-text-secondary hover:bg-subtle/60"}`}>
              <span className="flex items-center gap-2"><FolderKanban size={15} /> Tutti i documenti</span>
            </button>
            <button type="button" onClick={() => setActiveFolder(null)}
                    data-testid="docs-folder-root"
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeFolder === null ? "bg-subtle font-medium text-text-primary" : "text-text-secondary hover:bg-subtle/60"}`}>
              <span className="flex items-center gap-2"><Folder size={15} /> Radice</span>
              <span className="text-xs text-text-muted">{tree.root_count || ""}</span>
            </button>
            {tree.folders.map((f) => (
              <div key={f.id} className="group relative">
                <button type="button" onClick={() => setActiveFolder(f.id)}
                        data-testid={`docs-folder-${f.id}`}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                          activeFolder === f.id ? "bg-subtle font-medium text-text-primary" : "text-text-secondary hover:bg-subtle/60"}`}>
                  <span className="flex items-center gap-2 truncate">
                    <Folder size={15} className={f.visibility === "admin" ? "text-amber-500" : ""} />
                    <span className="truncate">{f.name}</span>
                    {f.visibility === "admin" && <Lock size={11} className="text-amber-500 flex-shrink-0" />}
                  </span>
                  <span className="text-xs text-text-muted">{f.file_count || ""}</span>
                </button>
                {isAdmin && (
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-0.5 bg-surface rounded">
                    <button type="button" className="p-1 text-text-muted hover:text-text-primary"
                            title="Rinomina" data-testid={`folder-edit-${f.id}`}
                            onClick={() => setFolderDialog({ folder: f })}>
                      <Pencil size={12} />
                    </button>
                    <button type="button" className="p-1 text-text-muted hover:text-red-500"
                            title="Elimina" data-testid={`folder-delete-${f.id}`}
                            onClick={() => deleteFolder(f)}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {treeFailed && tree.folders.length === 0 ? (
              <LoadErrorBox compact what="le cartelle" onRetry={loadTree} testId="docs-tree-error" />
            ) : tree.folders.length === 0 && (
              <div className="px-3 py-3 text-xs text-text-muted">Nessuna cartella{isAdmin ? ": creane una." : "."}</div>
            )}
          </div>
        </aside>

        {/* Contenuto */}
        <main className="lg:col-span-9">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Cerca per titolo, nome file o contenuto…"
                     className="pl-9" data-testid="docs-search" />
            </div>
            <button type="button" onClick={() => setOnlyUnread((v) => !v)}
                    data-testid="docs-unread-filter"
                    className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-sm transition-colors ${
                      onlyUnread
                        ? "border-amber-400 bg-amber-50 text-amber-700 font-medium"
                        : "border-border text-text-secondary hover:bg-subtle"}`}>
              <BookOpenCheck size={14} /> Da leggere
              {tree.unread_count > 0 && <span className="text-xs">({tree.unread_count})</span>}
            </button>
          </div>

          {activeFolderObj?.visibility === "admin" && (
            <div className="mb-3 text-xs text-amber-600 flex items-center gap-1.5" data-testid="folder-admin-note">
              <Lock size={12} /> Cartella riservata: visibile solo agli amministratori.
            </div>
          )}

          <div className="rounded-xl border border-border bg-surface overflow-hidden" data-testid="docs-table">
            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 size={22} className="animate-spin text-text-muted" /></div>
            ) : loadFailed ? (
              <LoadErrorBox what="i documenti" onRetry={loadFiles} testId="docs-load-error" />
            ) : files.length === 0 ? (
              <div className="py-16 text-center text-sm text-text-muted" data-testid="docs-empty">
                {onlyUnread ? "Niente da leggere: sei in pari ✓" : "Nessun documento qui."}
                {isAdmin && !onlyUnread && <div className="mt-1 text-xs">Carica il primo con “Carica documento”.</div>}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-subtle/70 text-text-secondary">
                  <tr className="text-left text-xs uppercase tracking-wider">
                    <th className="px-4 py-2.5 font-medium">Documento</th>
                    <th className="px-4 py-2.5 font-medium hidden md:table-cell">Caricato da</th>
                    <th className="px-4 py-2.5 font-medium hidden sm:table-cell">Data</th>
                    <th className="px-4 py-2.5 font-medium hidden sm:table-cell">Dimensione</th>
                    <th className="px-4 py-2.5 font-medium">Stato</th>
                    <th className="px-2 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {files.map((d) => (
                    <tr key={d.id} className="border-t border-border hover:bg-subtle/40 transition-colors"
                        data-testid={`doc-row-${d.id}`}>
                      <td className="px-4 py-3">
                        <button type="button" onClick={() => setPreview(d)}
                                className="flex items-center gap-2.5 text-left group"
                                data-testid={`doc-open-${d.id}`}>
                          <ExtIcon ext={d.ext} />
                          <span>
                            <span className="block text-text-primary font-medium group-hover:underline decoration-border underline-offset-2">
                              {d.title || d.filename}
                            </span>
                            <span className="block text-xs text-text-muted">{d.filename}</span>
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-text-secondary hidden md:table-cell">{d.uploaded_by_name}</td>
                      <td className="px-4 py-3 text-text-secondary hidden sm:table-cell">{fmtDate(d.uploaded_at)}</td>
                      <td className="px-4 py-3 text-text-secondary hidden sm:table-cell">{fmtSize(d.size_bytes)}</td>
                      <td className="px-4 py-3">
                        {d.requires_reading ? (
                          d.read_at ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium" data-testid={`doc-read-${d.id}`}>
                              <CheckCircle2 size={12} /> Letto
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-[11px] text-amber-700 font-medium" data-testid={`doc-toread-${d.id}`}>
                              Da leggere
                            </span>
                          )
                        ) : (
                          <span className="text-xs text-text-muted">—</span>
                        )}
                      </td>
                      <td className="px-2 py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          <button type="button" title="Anteprima" onClick={() => setPreview(d)}
                                  className="p-1.5 rounded text-text-muted hover:text-text-primary hover:bg-subtle"
                                  data-testid={`doc-preview-${d.id}`}>
                            <Eye size={15} />
                          </button>
                          <button type="button" title="Scarica" onClick={() => download(d)}
                                  className="p-1.5 rounded text-text-muted hover:text-text-primary hover:bg-subtle"
                                  data-testid={`doc-download-${d.id}`}>
                            <Download size={15} />
                          </button>
                          <button type="button" title="Chiedi all'AI" onClick={() => askAi(d)}
                                  disabled={askingId === d.id}
                                  className="p-1.5 rounded text-accent-strong hover:bg-accent-subtle"
                                  data-testid={`doc-askai-${d.id}`}>
                            {askingId === d.id ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                          </button>
                          {isAdmin && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button type="button" className="p-1.5 rounded text-text-muted hover:text-text-primary hover:bg-subtle"
                                        data-testid={`doc-menu-${d.id}`}>
                                  <MoreHorizontal size={15} />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => { setRenameDoc(d); setRenameTitle(d.title || ""); }}
                                                  data-testid={`doc-rename-${d.id}`}>
                                  <Pencil size={13} className="mr-2" /> Rinomina
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setMoveDoc(d); setMoveTarget(d.folder_id || ""); }}
                                                  data-testid={`doc-move-${d.id}`}>
                                  <FolderInput size={13} className="mr-2" /> Sposta
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => toggleRequired(d)} data-testid={`doc-required-${d.id}`}>
                                  <BookOpenCheck size={13} className="mr-2" />
                                  {d.requires_reading ? "Togli lettura obbligatoria" : "Lettura obbligatoria"}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setReadsDoc(d)} data-testid={`doc-reads-${d.id}`}>
                                  <Users size={13} className="mr-2" /> Chi l&apos;ha letto
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => deleteDoc(d)} className="text-red-600"
                                                  data-testid={`doc-delete-${d.id}`}>
                                  <Trash2 size={13} className="mr-2" /> Elimina
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </main>
      </div>

      {/* Dialogs */}
      {folderDialog && (
        <FolderDialog open onClose={() => setFolderDialog(null)} onSaved={refresh} folder={folderDialog.folder} />
      )}
      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onSaved={refresh}
                    folders={tree.folders} defaultFolderId={typeof activeFolder === "string" ? activeFolder : ""} />
      {preview && <PreviewModal doc={preview} onClose={() => setPreview(null)} onMarkedRead={markedRead} />}
      {readsDoc && <ReadsModal doc={readsDoc} onClose={() => setReadsDoc(null)} />}

      {renameDoc && (
        <Dialog open onOpenChange={(v) => !v && setRenameDoc(null)}>
          <DialogContent className="sm:max-w-sm" data-testid="rename-dialog">
            <DialogHeader><DialogTitle>Rinomina documento</DialogTitle></DialogHeader>
            <Input value={renameTitle} onChange={(e) => setRenameTitle(e.target.value)}
                   data-testid="rename-input" autoFocus
                   onKeyDown={(e) => e.key === "Enter" && saveRename()} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenameDoc(null)}>Annulla</Button>
              <Button onClick={saveRename} data-testid="rename-save-btn">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {moveDoc && (
        <Dialog open onOpenChange={(v) => !v && setMoveDoc(null)}>
          <DialogContent className="sm:max-w-sm" data-testid="move-dialog">
            <DialogHeader><DialogTitle>Sposta documento</DialogTitle></DialogHeader>
            <select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)}
                    data-testid="move-folder-select"
                    className="w-full h-9 rounded-md border border-border bg-surface px-3 text-sm text-text-primary">
              <option value="">Radice (nessuna cartella)</option>
              {tree.folders.map((f) => (
                <option key={f.id} value={f.id}>{f.name}{f.visibility === "admin" ? " (riservata)" : ""}</option>
              ))}
            </select>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveDoc(null)}>Annulla</Button>
              <Button onClick={saveMove} data-testid="move-save-btn">Sposta</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
